import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import { LineChart as MuiLineChart } from '@mui/x-charts/LineChart';
import { CHART_COLORS } from 'lib/functions/const/colors';
import React, { useEffect, useRef, useState } from 'react';

interface LineChartProps {
  data: {
    name: string;
    [key: string]: string | number;
  }[];
  colors?: string[];
  aspect?: number;
  height?: number;
  fullWidth?: boolean;
  showLegend?: boolean;
  formatYAxis?: (value: number) => string;
  yAxisFormat?: 'K' | 'M' | 'MM' | 'auto';
  showArea?: boolean;
}

const LineChart: React.FC<LineChartProps> = ({
  data,
  colors = Object.values(CHART_COLORS),
  aspect = 4.0 / 1.5,
  height,
  fullWidth = true,
  showLegend = true,
  formatYAxis,
  yAxisFormat = 'auto',
  showArea = true,
}) => {
  const [visibleSeries, setVisibleSeries] = useState<string[]>([]);
  const knownDataKeysRef = useRef<string[]>([]);

  const getFormatFunction = () => {
    if (formatYAxis) return formatYAxis;

    switch (yAxisFormat) {
      case 'K':
        return (value: number) => {
          if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
          return value.toString();
        };
      case 'M':
        return (value: number) => {
          if (value >= 1000000) return `${(value / 1000000).toFixed(0)}M`;
          if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
          return value.toString();
        };
      case 'MM':
        return (value: number) => {
          if (value >= 1000000000)
            return `${(value / 1000000000).toFixed(0)}MM`;
          if (value >= 1000000) return `${(value / 1000000).toFixed(0)}M`;
          if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
          return value.toString();
        };
      case 'auto':
      default:
        return (value: number) => {
          if (value >= 1000000000)
            return `${(value / 1000000000).toFixed(0)}MM`;
          if (value >= 1000000) return `${(value / 1000000).toFixed(0)}M`;
          if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
          return value.toString();
        };
    }
  };

  const finalFormatYAxis = getFormatFunction();

  const dataKeys =
    data.length > 0 ? Object.keys(data[0]).filter((key) => key !== 'name') : [];

  useEffect(() => {
    const prevKnown = knownDataKeysRef.current;
    const newKeys = dataKeys.filter((k) => !prevKnown.includes(k));

    if (newKeys.length > 0) {
      setVisibleSeries((prevVisible) => {
        if (prevVisible.length === 0) return dataKeys;
        return [...prevVisible, ...newKeys];
      });
      knownDataKeysRef.current = [...prevKnown, ...newKeys];
    }
  }, [dataKeys]);

  const handleSeriesToggle = (seriesKey: string) => {
    setVisibleSeries((prev) => {
      if (prev.includes(seriesKey)) {
        const newVisible = prev.filter((key) => key !== seriesKey);
        return newVisible;
      } else {
        const newVisible = [...prev, seriesKey];
        return newVisible;
      }
    });
  };

  const series = dataKeys
    .map((key, index) => ({
      id: key,
      label: key,
      data: data.map((item) => item[key] as number),
      color: colors[index % colors.length],
      area: showArea,
      showMark: true,
      curve: 'linear' as const,
      areaOpacity: showArea ? 0.3 : 0,
      valueFormatter: (v: number | null) =>
        v === null ? '' : finalFormatYAxis(v),
    }))
    .filter((series) => visibleSeries.includes(series.id));

  const xLabels = data.map((item) => item.name);

  const chartWidth = fullWidth
    ? undefined
    : height
      ? height * aspect
      : undefined;
  const chartHeight = height || 400;

  return (
    <Box sx={{ width: '100%' }}>
      {showLegend && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
            padding: '10px 0',
            marginBottom: '10px',
            flexWrap: 'wrap',
          }}
        >
          {visibleSeries.length === 0 && (
            <Box
              onClick={() => setVisibleSeries(dataKeys)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                padding: '4px 8px',
                borderRadius: '4px',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                },
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: '#B0B0B0',
                  fontSize: '12px',
                  fontWeight: 500,
                }}
              >
                Mostrar todas
              </Typography>
            </Box>
          )}

          {dataKeys.map((key, index) => (
            <Box
              key={key}
              onClick={() => handleSeriesToggle(key)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                opacity: visibleSeries.includes(key) ? 1 : 0.5,
                padding: '4px 8px',
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  backgroundColor: colors[index % colors.length],
                  borderRadius: '2px',
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: '#B0B0B0',
                  fontSize: '12px',
                  textDecoration: visibleSeries.includes(key)
                    ? 'none'
                    : 'line-through',
                }}
              >
                {key}
              </Typography>
              <IconButton
                size="small"
                sx={{
                  color: visibleSeries.includes(key) ? '#E0E0E0' : '#666',
                  padding: '2px',
                }}
              >
                {visibleSeries.includes(key) ? (
                  <Visibility fontSize="small" />
                ) : (
                  <VisibilityOff fontSize="small" />
                )}
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      <MuiLineChart
        width={chartWidth}
        height={chartHeight}
        series={series}
        margin={{
          top: 20,
          right: 30,
          bottom: 20,
        }}
        xAxis={[
          {
            scaleType: 'point',
            data: xLabels,
            tickLabelStyle: {
              fill: '#B0B0B0',
              fontSize: 12,
            },
          },
        ]}
        yAxis={[
          {
            tickLabelStyle: {
              fill: '#B0B0B0',
              fontSize: 12,
            },
            valueFormatter: finalFormatYAxis,
            labelStyle: {
              fill: '#B0B0B0',
            },
          },
        ]}
        grid={{
          horizontal: true,
          vertical: true,
        }}
      />
    </Box>
  );
};

export default LineChart;
